import { useTranslation } from 'react-i18next';
import './UpgradeToPremium.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Checkmark } from 'images/icons/Checkmark.svg';
import { Button } from '@trackman/web-shared-components';
import { useCartStore } from 'stores/SubscriptionsAndPricing/CartStore';
import { CartItem } from 'types/cart';
import { AddedToCart } from '../../AddedToCart/AddedToCart';
import { subscription } from '../Shop';
import { CartButton } from '../CartButton/CartButton';
import { useCallback } from 'react';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';

export const UpgradeToPremium = () => {
  const { t } = useTranslation();
  const itemsInCart: CartItem[] = useCartStore((state) => state.items);
  const addItemToCart = useCartStore((state) => state.addItemToCart);

  const handleAddToCart = useCallback(() => {
    addItemToCart(subscription);
    addCartItemToLocalStorage(subscription);
  }, [addItemToCart, itemsInCart]);

  return (
    <div className='upgrade-to-premium'>
      <CartButton />
      <Subtitle>
        <>{t('UpgradeToPremium', { ns: 'subscriptionsAndPricing' })}</>
      </Subtitle>

      <div className='plans-container'>
        <FreePlanCard />
        <PremiumPlanCard isItemInCart={itemsInCart.some((item) => item.id === 'premium')} addItemToCart={handleAddToCart} />
      </div>
    </div>
  );
};

const BenefitBulletPoint = ({ isPremiumPlan }: { isPremiumPlan: boolean }) => {
  return (
    <div
      className={isPremiumPlan ? 'benefit-bullet-point benefit-bullet-point__premium' : 'benefit-bullet-point benefit-bullet-point__free'}
    >
      <Checkmark />
    </div>
  );
};

const FreePlanCard = () => {
  const { t } = useTranslation();

  return (
    <div className='plan-card'>
      <div className='free-plan-badge'>{t('CurrentPlan', { ns: 'subscriptionsAndPricing' })}</div>
      <Subtitle>{t('Basic', { ns: 'subscriptionsAndPricing' })}</Subtitle>

      <h2>{t('Free', { ns: 'subscriptionsAndPricing' })}</h2>
      <p>{t('GainAccessToResources', { ns: 'subscriptionsAndPricing' })}</p>
      <div className='benefits-list'>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('LearningMaterialsAndParameters', { ns: 'subscriptionsAndPricing' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('PayPerEvent', { ns: 'subscriptionsAndPricing' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('PayPerCertificate', { ns: 'subscriptionsAndPricing' })}
        </div>
      </div>
    </div>
  );
};

const PremiumPlanCard = ({ isItemInCart, addItemToCart }: { isItemInCart: boolean; addItemToCart: () => void }) => {
  const { t } = useTranslation();
  return (
    <div className='plan-card premium-card'>
      <div className='premium-card-content-wrapper'>
        <Subtitle>
          <>
            <div className='premium-plan-header'>
              <Crown className='premium-plan-icon' />
              {t('Premium', { ns: 'subscriptionsAndPricing' })}
            </div>
          </>
        </Subtitle>

        <h2>
          {t('Price175', { ns: 'subscriptionsAndPricing' })}
          <span className=''>{t('Year', { ns: 'subscriptionsAndPricing' })}</span>
        </h2>

        <p>{t('GainAccessToMoreToolsAndGoodies', { ns: 'subscriptionsAndPricing' })}</p>
        <div className='benefits-list'>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('-50%OffCertificates', { ns: 'subscriptionsAndPricing' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('LearningMaterialsAndParameters', { ns: 'subscriptionsAndPricing' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('EventsFreeOfCharge', { ns: 'subscriptionsAndPricing' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('ParticipateInLeagues', { ns: 'subscriptionsAndPricing' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('AccessToOnlineLearningPlatform', { ns: 'subscriptionsAndPricing' })}
          </div>
        </div>
      </div>

      {!isItemInCart ? (
        <Button className='button' variant='contained' onClick={() => addItemToCart()}>
          {t('Subscribe', { ns: 'subscriptionsAndPricing' })}
        </Button>
      ) : (
        <AddedToCart />
      )}
    </div>
  );
};
